/**
 * Cliente de la API de Flotas (tag `clientes-flotas` de `openapi.yaml`) — Admin Web.
 *
 * Endpoints (US-2.3, ADR 0047):
 * - GET    /api/v1/admin/fleets
 * - POST   /api/v1/admin/fleets
 * - GET    /api/v1/admin/fleets/{fleetId}
 * - PATCH  /api/v1/admin/fleets/{fleetId}
 * - GET    /api/v1/admin/fleets/{fleetId}/drivers
 * - POST   /api/v1/admin/fleets/{fleetId}/drivers
 * - PATCH  /api/v1/admin/fleets/{fleetId}/drivers/{customerId}
 * - DELETE /api/v1/admin/fleets/{fleetId}/drivers/{customerId}
 * - GET    /api/v1/admin/fleets/{fleetId}/vehicles
 * - POST   /api/v1/admin/fleets/{fleetId}/vehicles
 * - PATCH  /api/v1/admin/fleets/{fleetId}/vehicles/{vehicleId}
 * - DELETE /api/v1/admin/fleets/{fleetId}/vehicles/{vehicleId}
 *
 * Los tipos de datos se derivan del contrato en `@goelectric/shared-types`; la red vive
 * aquí (mismo patrón que `customerApi`/`allyApi`, sobre `apiRequest`).
 */
import type {
  CreateFleetBody,
  Fleet,
  FleetDriver,
  FleetDriverCreateRequest,
  FleetDriverUpdateRequest,
  FleetVehicle,
  FleetVehicleCreateRequest,
  FleetVehicleUpdateRequest,
  FleetsPage,
  ListFleetsParams,
  PageMeta,
  UpdateFleetBody,
} from '@goelectric/shared-types';
import { apiRequest } from '@/lib/http/apiClient';

/** Query params del listado de flotas, tal como los define el contrato. */
export type FleetListParams = ListFleetsParams;

/** Paginación de los subrecursos de conductores/vehículos. */
export interface FleetPeopleParams {
  offset?: number;
  limit?: number;
}

/** Página de conductores de una flota (`GET .../drivers`). */
export interface FleetDriversPage {
  data: FleetDriver[];
  meta: PageMeta;
}

/** Página de vehículos de una flota (`GET .../vehicles`). */
export interface FleetVehiclesPage {
  data: FleetVehicle[];
  meta: PageMeta;
}

/** Serializa los params definidos (omite `undefined`/`null`/vacío) a `?a=b&c=d`. */
function toQuery(params: object): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value));
    }
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

/** Codifica un id para usarlo como segmento de ruta. */
function segment(value: string): string {
  return encodeURIComponent(value);
}

const FLEETS_PATH = '/api/v1/admin/fleets';

export const fleetApi = {
  list(params: FleetListParams = {}): Promise<FleetsPage> {
    return apiRequest<FleetsPage>(`${FLEETS_PATH}${toQuery(params)}`, {
      retryOnUnauthorized: true,
    });
  },

  get(fleetId: string): Promise<Fleet> {
    return apiRequest<Fleet>(`${FLEETS_PATH}/${segment(fleetId)}`);
  },

  create(body: CreateFleetBody): Promise<Fleet> {
    return apiRequest<Fleet>(FLEETS_PATH, { method: 'POST', body });
  },

  update(fleetId: string, body: UpdateFleetBody): Promise<Fleet> {
    return apiRequest<Fleet>(`${FLEETS_PATH}/${segment(fleetId)}`, { method: 'PATCH', body });
  },

  listDrivers(fleetId: string, params: FleetPeopleParams = {}): Promise<FleetDriversPage> {
    return apiRequest<FleetDriversPage>(
      `${FLEETS_PATH}/${segment(fleetId)}/drivers${toQuery(params)}`,
    );
  },

  /**
   * Vincula un conductor existente (`customerId`) o da de alta uno inline
   * (`fullName` + `phone`) según el contrato `FleetDriverCreateRequest`.
   */
  linkDriver(fleetId: string, body: FleetDriverCreateRequest): Promise<FleetDriver> {
    return apiRequest<FleetDriver>(`${FLEETS_PATH}/${segment(fleetId)}/drivers`, {
      method: 'POST',
      body,
    });
  },

  updateDriver(
    fleetId: string,
    customerId: string,
    body: FleetDriverUpdateRequest,
  ): Promise<FleetDriver> {
    return apiRequest<FleetDriver>(
      `${FLEETS_PATH}/${segment(fleetId)}/drivers/${segment(customerId)}`,
      { method: 'PATCH', body },
    );
  },

  /** Desvincula al conductor (el cliente no se elimina). */
  unlinkDriver(fleetId: string, customerId: string): Promise<void> {
    return apiRequest<void>(
      `${FLEETS_PATH}/${segment(fleetId)}/drivers/${segment(customerId)}`,
      { method: 'DELETE' },
    );
  },

  listVehicles(fleetId: string, params: FleetPeopleParams = {}): Promise<FleetVehiclesPage> {
    return apiRequest<FleetVehiclesPage>(
      `${FLEETS_PATH}/${segment(fleetId)}/vehicles${toQuery(params)}`,
    );
  },

  createVehicle(fleetId: string, body: FleetVehicleCreateRequest): Promise<FleetVehicle> {
    return apiRequest<FleetVehicle>(`${FLEETS_PATH}/${segment(fleetId)}/vehicles`, {
      method: 'POST',
      body,
    });
  },

  updateVehicle(
    fleetId: string,
    vehicleId: string,
    body: FleetVehicleUpdateRequest,
  ): Promise<FleetVehicle> {
    return apiRequest<FleetVehicle>(
      `${FLEETS_PATH}/${segment(fleetId)}/vehicles/${segment(vehicleId)}`,
      { method: 'PATCH', body },
    );
  },

  /** Baja lógica: el vehículo queda inactivo y su RFID se bloquea en CitrineOS. */
  deactivateVehicle(fleetId: string, vehicleId: string): Promise<void> {
    return apiRequest<void>(
      `${FLEETS_PATH}/${segment(fleetId)}/vehicles/${segment(vehicleId)}`,
      { method: 'DELETE' },
    );
  },
};
